import gsap from 'gsap';
import CustomEase from 'gsap/dist/CustomEase';
import ObjectsHolder from '../ObjectsHolder';
import { radians } from '../../../../utils/degreesAndRadians';
import { findChildren } from '../../../../utils/objects3D';

gsap.registerPlugin(CustomEase);

const THREE = require('three');

const pushWingsUnitToArray = (arr, right, left) => {
    if (right) arr.push({ target: right, sign: 1, coord: 'z' });
    if (left) arr.push({ target: left, sign: -1, coord: 'z' });
};

class AnimatedDragonflyWingsObject3D extends ObjectsHolder {
    constructor(data, webgl, obj3D, reflection3D = null) {
        super(data, webgl, {
            obj3D, reflection3D
        });
    }

    _setup(data, stgs, webgl) {
        super._setup(data, stgs, webgl);

        this.hasInvalidate = true;
        this.hasTweens = true;
    }

    _constructView(data, webgl, stgs, factory, orientation) {
        super._constructView(data, webgl, stgs, factory, orientation);

        const { scaleTweenOnScroll } = data;

        const { obj3D, reflection3D } = stgs;

        this.add(obj3D);

        this.obj3D = obj3D;

        let reflectionShell = null;

        if (reflection3D) {
            reflectionShell = new THREE.Group();
            reflectionShell.add(reflection3D);
        }

        // You need to inject reflectionShell instead of reflection3D. i.e. you need to substitute it
        this.reflection3D = reflection3D;
        this.reflectionShell = reflectionShell;

        const rightFrontWing = findChildren(obj3D, 'dfly_wing_right_front', true);
        const leftTopWing = findChildren(obj3D, 'dfly_wing_left_front', true);
        const rightBackWing = findChildren(obj3D, 'dfly_wing_right_back', true);
        const leftBackWing = findChildren(obj3D, 'dfly_wing_left_back', true);

        const rightFrontWingSync = findChildren(reflection3D, 'dfly_wing_right_front', true);
        const leftFrontWingSync = findChildren(reflection3D, 'dfly_wing_left_front', true);
        const rightBackWingSync = findChildren(reflection3D, 'dfly_wing_right_back', true);
        const leftBackWingSync = findChildren(reflection3D, 'dfly_wing_left_back', true);

        this.topWings = [];
        pushWingsUnitToArray(this.topWings, rightFrontWing, leftTopWing);
        pushWingsUnitToArray(this.topWings, rightFrontWingSync, leftFrontWingSync);

        this.backWings = [];
        pushWingsUnitToArray(this.backWings, rightBackWing, leftBackWing);
        pushWingsUnitToArray(this.backWings, rightBackWingSync, leftBackWingSync);

        this.state = {
            rotation: 15,
            rotationX: -0.5,
            rotationY: -5,
            rotationBack: 15 * 0.9,
            rotationBackX: -0.5,
            rotationBackY: -5,
            strength: 1,
            rotationChanged: true
        };

        this.settings = {
            defaultTimeScale: 0.5,
            maxTimeScale: scaleTweenOnScroll ?? 0.5,
            canChangeScale: typeof scaleTweenOnScroll === 'number' && !Number.isNaN(scaleTweenOnScroll)
        };
    }

    _start() {
        const tl = gsap.timeline({
            repeat: -1
        });

        const onUpdate = () => {
            this.state.rotationChanged = true;
        };

        const tlFront = gsap.timeline({
            repeat: -1
        });
        const tlBack = gsap.timeline({
            repeat: -1
        });
        const tlBody = gsap.timeline({
            repeat: -1
        });

        tlBody.to(this.obj3D.position, {

        });

        tlBack.to(this.state, {
            duration: 1,
            rotationBack: -35 * 0.9,
            rotationBackX: 1,
            rotationBackY: 10,
            ease: 'sine.inOut',
            onUpdate
        });

        tlBack.to(this.state, {
            duration: 1.1,
            rotationBack: 15 * 0.9,
            rotationBackX: -0.5,
            rotationBackY: -5,
            ease: 'sine.inOut',
            onUpdate
        });

        tlFront.to(this.state, {
            duration: 1,
            rotation: -35,
            rotationX: 1,
            rotationY: 10,
            ease: 'sine.inOut',
            onUpdate
        });

        tlFront.to(this.state, {
            duration: 1.1,
            rotation: 15,
            rotationX: -0.5,
            rotationY: -5,
            ease: 'sine.inOut',
            onUpdate
        });

        tl.add(tlFront, -0.5);
        tl.add(tlBack, -0.25);

        tl.timeScale(this.settings.defaultTimeScale);

        this.mainTimeline = tl;
    }

    invalidate(t, dt) {
        super.invalidate(t, dt);

        if (this.state.rotationChanged) {
            const {
                rotation,
                rotationX,
                rotationY,
                rotationBack,
                rotationBackX,
                rotationBackY,
                strength
            } = this.state;

            this.topWings.forEach((obj) => {
                obj.target.rotation.z = strength * obj.sign * radians(rotation);
                obj.target.rotation.x = radians(rotationX);
                obj.target.rotation.y = obj.sign * radians(rotationY);
            });

            this.backWings.forEach((obj) => {
                obj.target.rotation.z = strength * obj.sign * radians(rotationBack);
                obj.target.rotation.x = radians(rotationBackX);
                obj.target.rotation.y = obj.sign * radians(rotationBackY);
            });
        }

        this.state.rotationChanged = false;
    }

    set progress(value) {
        const progressChanged = super.progress !== value;

        super.progress = value;

        if (!progressChanged || !this.settings.canChangeScale) return;

        this._speedUp();
        this._slowDown();
    }

    _speedUp() {
        if (!this.mainTimeline) return;
        if (this.isFast) return;

        const tl = gsap.timeline();

        tl.to(this.mainTimeline, {
            duration: 0.15,
            timeScale: this.settings.maxTimeScale
        });
        tl.to(this.state, {
            duration: 0.25,
            strength: 1.7
        });

        this.speedUpTimeline = tl;
        this.isFast = true;
    }

    _slowDown(delay = 0.1) {
        if (!this.mainTimeline) return;

        if (this.slowDownTimeline) this.slowDownTimeline.kill();

        const tl = gsap.timeline();

        tl.to(this.mainTimeline, {
            delay,
            duration: 0.5,
            timeScale: this.settings.defaultTimeScale,
            onStart: () => {
                this.speedUpTimeline.kill();
                this.speedUpTimeline = null;
                this.isFast = false;
            },
            onComplete: () => {
                this.slowDownTimeline = null;
            }
        });
        tl.to(this.state, {
            duration: 0.25,
            strength: 0.8
        });

        this.slowDownTimeline = tl;
    }
}

export default AnimatedDragonflyWingsObject3D;
